// src/components/PreInterviewViewModal.js

import React from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Box, Divider, Chip, Stack, Grid
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import EventIcon from '@mui/icons-material/Event';

import { getStatusChipColor, normalizePreInterviewDurum, arsivDurumlari } from './PreInterviewConstants';
import { parseNotlarYeniden, notTarihiToDate, formatNotTarihi } from './PreInterviewNotes';

const formatTarih = (timestamp) => {
    const date = notTarihiToDate(timestamp);
    if (!date) return '-';
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const PreInterviewViewModal = ({ isOpen, onClose, interview, onEdit, onCreateWorkOrder }) => {
    if (!interview) return null;

    const durum = normalizePreInterviewDurum(interview.durum);
    const showCreateButton = !arsivDurumlari.includes(durum);
    const notlar = parseNotlarYeniden(interview.notlar);
    const hatirlatma = notTarihiToDate(interview.hatirlatmaTarihi);
    const hatirlatmaGecti = Boolean(hatirlatma) && hatirlatma.getTime() < Date.now();

    const handleCreateAppointment = () => {
        onCreateWorkOrder(interview);
        onClose();
    };

    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h6">{interview.adSoyad}</Typography>
                    <Typography variant="body2" color="text.secondary">{interview.telefon}</Typography>
                </Box>
                <Chip
                    label={durum}
                    color={getStatusChipColor(durum)}
                    size="small"
                />
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={3}>
                    {/* Araç Bilgileri */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="primary" gutterBottom>Araç Bilgileri</Typography>
                        <Stack spacing={1}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="text.secondary">Plaka:</Typography>
                                {interview.plakaBekleniyor && !interview.aracPlaka ? (
                                    <Chip label="Plaka Bekleniyor" size="small" color="warning" variant="outlined" />
                                ) : (
                                    <Typography variant="body2" fontWeight="bold">{interview.aracPlaka || '-'}</Typography>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">Marka/Model:</Typography>
                                <Typography variant="body2">{interview.aracMarka} {interview.aracModel}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">Yıl:</Typography>
                                <Typography variant="body2">{interview.aracYil || '-'}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">Araç Tipi:</Typography>
                                <Typography variant="body2">{interview.aracTuru || '-'}</Typography>
                            </Box>
                        </Stack>
                    </Grid>

                    {/* Tarihler */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="primary" gutterBottom>Tarihler</Typography>
                        <Stack spacing={1}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">Görüşme Tarihi:</Typography>
                                <Typography variant="body2">{formatTarih(interview.createdAt)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">Tahmini Teslim Alım:</Typography>
                                <Typography variant="body2" fontWeight="bold">{formatTarih(interview.teslimTarihi)}</Typography>
                            </Box>
                            {hatirlatma && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Hatırlatma:</Typography>
                                    <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        color={hatirlatmaGecti ? 'error.main' : 'text.primary'}
                                    >
                                        {formatNotTarihi(interview.hatirlatmaTarihi)}{hatirlatmaGecti ? ' (geçti)' : ''}
                                    </Typography>
                                </Box>
                            )}
                        </Stack>
                    </Grid>

                    {/* Hizmetler */}
                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" color="primary" gutterBottom>İlgilenilen Hizmetler</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {interview.ilgilenilenHizmetler?.map((hizmet, index) => (
                                <Chip key={index} label={hizmet} size="small" variant="outlined" />
                            )) || <Typography variant="body2" color="text.secondary">-</Typography>}
                        </Box>
                    </Grid>

                    {/* Fiyat Bilgisi */}
                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold">Teklif Toplamı (KDV hariç):</Typography>
                            <Typography variant="h5" color="primary.main" fontWeight="bold">
                                {(interview.toplamTutar || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                            </Typography>
                        </Box>
                    </Grid>

                    {/* Notlar — en yeni üstte; eski tek-string kayıtlar "tarihsiz" olarak görünür */}
                    {notlar.length > 0 && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                                Notlar ({notlar.length})
                            </Typography>
                            <Stack spacing={1}>
                                {notlar.map((not, index) => {
                                    const tarihMetni = formatNotTarihi(not.tarih);
                                    return (
                                        <Box key={index} sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                {not.metin}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {tarihMetni || 'Eski kayıt (tarihsiz)'}{not.yazan ? ` · ${not.yazan}` : ''}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                <Button onClick={onClose}>Kapat</Button>
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => { onEdit(interview); onClose(); }}
                    >
                        Düzenle
                    </Button>
                    {showCreateButton && (
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<EventIcon />}
                            onClick={handleCreateAppointment}
                        >
                            Randevu Oluştur
                        </Button>
                    )}
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default PreInterviewViewModal;
